import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import {
  PropagationNote,
  StatusNote,
} from 'components/domain/customDomains/modal/CustomDomainModal.styles';
import CustomDomainSection from 'components/domain/customDomains/modal/CustomDomainSection';
import useMigrateAgencyDnsSettings from 'components/domain/customDomains/useMigrateAgencyDnsSettings';
import {
  AgencyDnsSettingStatus,
  ApplicationDnsSetting,
  WhitelabelSubjectApplication,
} from 'models/CustomDomains';
import {
  MigrationActionsStyled,
  MigrationStyled,
} from '../WhiteLabelSettings.styles';

/**
 * The PMP-16478 section rendered on the page instead of in its modal, against the same
 * dns-settings API. A domain only serves once the backend has verified DNS and provisioned
 * a CloudFront tenant, which is why the section keeps its own Save.
 *
 * A LEGACY domain (one still served from the agency columns) is shown read-only with a
 * Validate button, the same migration the modal drives from its footer.
 */
const BookingCustomDomainSection = ({
  dnsSetting,
  isCheckingConfiguration,
  onCheckConfiguration,
}: {
  dnsSetting?: ApplicationDnsSetting;
  isCheckingConfiguration: boolean;
  onCheckConfiguration: () => void;
}) => {
  const { t } = useTranslation();
  const { migrateDnsSettings, isMigrating } = useMigrateAgencyDnsSettings();

  const legacySetting =
    dnsSetting?.status === AgencyDnsSettingStatus.LEGACY
      ? dnsSetting
      : undefined;

  return (
    <>
      <CustomDomainSection
        application={WhitelabelSubjectApplication.DBS}
        dnsSetting={dnsSetting}
        isCheckingConfiguration={isCheckingConfiguration}
        isMigration={!!legacySetting}
        onCheckConfiguration={onCheckConfiguration}
      />

      {legacySetting && (
        <MigrationStyled data-testid="booking-custom-domain-migration">
          <StatusNote>
            {t('pageDirectBookingEngine.whiteLabelSettings.migrateNote')}
          </StatusNote>
          <PropagationNote>
            {t('componentCustomDomains.propagationNote')}
          </PropagationNote>
          <MigrationActionsStyled>
            <Button
              bsStyle="success"
              data-testid="booking-custom-domain-validate"
              disabled={isMigrating}
              onClick={() => migrateDnsSettings([legacySetting])}
            >
              {t('componentCustomDomains.validate')}
            </Button>
          </MigrationActionsStyled>
        </MigrationStyled>
      )}
    </>
  );
};

BookingCustomDomainSection.defaultProps = {
  dnsSetting: undefined,
};

export default BookingCustomDomainSection;
